import { motion } from "framer-motion";
import { Instagram, Mail, ArrowUpRight, Phone } from "lucide-react";

export const Contact = ({ onBookClick }) => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-32"
    >
      <div
        className="orb orb-sand"
        style={{ width: 460, height: 460, top: -80, right: -160 }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
              Get in touch
            </p>
            <h2
              data-testid="contact-title"
              className="font-serif text-3xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#2D332F] leading-[1.05]"
            >
              Reach out today.
              <br />
              <em className="text-[#739072]">Let&rsquo;s start the conversation.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#5C6661] text-lg leading-relaxed">
            Send a direct message on Instagram, email Kathleen, or call directly
            for new client inquiries, telehealth scheduling and clinical
            consultations.{" "}
            <button
              onClick={onBookClick}
              data-testid="contact-book-inline"
              className="underline text-[#739072] hover:text-[#5C765B] transition"
            >
              Or request an appointment online &rarr;
            </button>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <motion.a
            href="tel:+15187955154"
            data-testid="contact-phone-us"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="group flex items-center gap-5 rounded-3xl border border-black/5 bg-[#FDFCF8] p-6 md:p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
          >
            <div className="w-11 h-11 shrink-0 rounded-full bg-[#F3F1E7] flex items-center justify-center group-hover:bg-[#739072] transition-colors duration-500">
              <Phone
                size={18}
                strokeWidth={1.5}
                className="text-[#739072] group-hover:text-white transition-colors duration-500"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-1">
                Call &mdash; United States
              </p>
              <p className="font-serif text-2xl md:text-3xl text-[#2D332F]">
                +1 (518) 795-5154
              </p>
            </div>
            <ArrowUpRight
              size={20}
              strokeWidth={1.5}
              className="text-[#2D332F]/40 group-hover:text-[#739072] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500"
            />
          </motion.a>

          <motion.a
            href="tel:+353873773620"
            data-testid="contact-phone-ie"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="group flex items-center gap-5 rounded-3xl border border-black/5 bg-[#FDFCF8] p-6 md:p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
          >
            <div className="w-11 h-11 shrink-0 rounded-full bg-[#F3F1E7] flex items-center justify-center group-hover:bg-[#739072] transition-colors duration-500">
              <Phone
                size={18}
                strokeWidth={1.5}
                className="text-[#739072] group-hover:text-white transition-colors duration-500"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-1">
                Call &mdash; Ireland
              </p>
              <p className="font-serif text-2xl md:text-3xl text-[#2D332F]">
                +353 87 377 3620
              </p>
            </div>
            <ArrowUpRight
              size={20}
              strokeWidth={1.5}
              className="text-[#2D332F]/40 group-hover:text-[#739072] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500"
            />
          </motion.a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.a
            href="https://instagram.com/Neurocompartners"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-instagram"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="group relative overflow-hidden rounded-3xl border border-black/5 bg-[#FDFCF8] p-10 md:p-12 hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
          >
            <div className="flex items-start justify-between mb-12">
              <div className="w-12 h-12 rounded-full bg-[#F3F1E7] flex items-center justify-center group-hover:bg-[#739072] transition-colors duration-500">
                <Instagram
                  size={20}
                  strokeWidth={1.5}
                  className="text-[#739072] group-hover:text-white transition-colors duration-500"
                />
              </div>
              <ArrowUpRight
                size={22}
                strokeWidth={1.5}
                className="text-[#2D332F]/40 group-hover:text-[#739072] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
              />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-3">
              Follow &amp; message
            </p>
            <p className="font-serif text-3xl md:text-4xl text-[#2D332F]">
              @Neurocompartners
            </p>
            <p className="mt-3 text-[#5C6661]">
              Direct message for fastest response.
            </p>
          </motion.a>

          <motion.a
            href="mailto:neurocompartners@gmail.com"
            data-testid="contact-email"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="group relative overflow-hidden rounded-3xl border border-black/5 bg-[#2D332F] text-[#FDFCF8] p-10 md:p-12 hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
          >
            <div className="flex items-start justify-between mb-12">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#D4B996] transition-colors duration-500">
                <Mail
                  size={20}
                  strokeWidth={1.5}
                  className="text-[#D4B996] group-hover:text-[#2D332F] transition-colors duration-500"
                />
              </div>
              <ArrowUpRight
                size={22}
                strokeWidth={1.5}
                className="text-white/50 group-hover:text-[#D4B996] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
              />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#FDFCF8]/60 mb-3">
              Email Kathleen
            </p>
            <p className="font-serif text-3xl md:text-4xl break-words">
              neurocompartners@gmail.com
            </p>
            <p className="mt-3 text-[#FDFCF8]/70">
              For new clients, referrals &amp; consultations.
            </p>
          </motion.a>
        </div>
      </div>
    </section>
  );
};
